import './index.scss'

import { useState } from 'react'

import { useAuth } from '@auth/AuthProvider'
import Modal, { ModalButtonStyle } from '@components/Modal'
import PlaylistsController from '@data/controller/PlaylistsController'
import Playlist from '@data/interfaces/playlist.interface'

import closeIcon from 'url:@icon/close.svg';
import warningIcon from 'url:@icon/error-icon.svg';

const HTTP_NOT_FOUND = 404;

// Styled after the service and Launchpad delete modals.
export default function DeletePlaylistModal(props: {
  isOpen: boolean,
  playlist: Playlist,
  close: () => void,
  onDeleted: () => void
}) {
  const auth = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const name = props.playlist.attributes.name || 'this playlist';

  const deletePlaylist = async () => {
    if (!auth.churchId) {
      setError('Missing church context. Please refresh and try again.');
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      const response = await PlaylistsController.deletePlaylist(auth.churchId, props.playlist.id, auth);

      // Already gone, most likely deleted elsewhere: the outcome asked for.
      if (response.error && response.status !== HTTP_NOT_FOUND) {
        setError(response.error);
        return;
      }

      props.onDeleted();
    } finally {
      setIsDeleting(false);
    }
  };

  return <Modal
    id='delete-playlist-modal'
    isOpen={props.isOpen}
    footerButtons={[
      {
        title: isDeleting ? 'Deleting...' : 'Delete Playlist',
        style: ModalButtonStyle.FILLED,
        enabled: !isDeleting,
        onClick: () => {
          void deletePlaylist();
        }
      },
      {
        title: 'Cancel',
        style: ModalButtonStyle.OUTLINE,
        enabled: !isDeleting,
        onClick: props.close
      }
    ]}>
    <div className='delete-playlist-modal-content'>
      <div className='header'>
        <button
          type='button'
          className='close'
          disabled={isDeleting}
          onClick={props.close}>
          <img className='icon' src={closeIcon} alt='Close icon' />
        </button>

        <h3 className='title'>Delete Playlist</h3>
      </div>

      {error ? <p className='save-error'>{error}</p> : null}

      <div className='body'>
        <img src={warningIcon} alt='' aria-hidden='true' className='warning-icon' />

        <p className='warning-copy'>
          <strong>Are you sure you want to delete {name}?</strong>
        </p>

        <p className='warning-copy'>
          It will be removed from Launchpad and the Salt app, along with its songs. Anyone with a link to it will
          no longer be able to open it. This action cannot be undone.
        </p>
      </div>
    </div>
  </Modal>
}
